'use client'
import { useMemoizedContentGetter } from 'utils/contentful'
import { TitleDivider } from 'components/TitleDivider'
import { useViewportSmallerThan } from 'utils/viewport'
import { BREAKPOINTS } from 'constants/viewport'
import { Box, Link } from '@achieve/ascend'
import { TrackingWrapper } from 'components/Tracking/TrackingWrapper'
import { _get } from 'utils/shared'
import { ArrowLeft } from 'react-feather'
import { LinkButton, MediaImageStandard, Typography } from 'components/Contentful'
import { Eyebrow } from 'components/Eyebrow'
import { IconListItem } from 'components/IconListItem'
import { Carousel } from 'components/Carousels'
import { ANCHOR_POINTS, AchieveBadge } from 'components/AchieveBadge'
import { useEffect, useState } from 'react'

function RecommendedCards({
  content,
  onReset,
  formAmount,
  formHomeowner,
  formAmountQueryParameter,
  sectionVariationObject,
}) {
  const isMobile = useViewportSmallerThan(BREAKPOINTS.xl)

  const [topChoice, setTopChoice] = useState('')
  const [orderCards, setOrderCards] = useState('')
  const [gridItems, setGridItems] = useState([])

  const { titleResult, eyebrowResult, gridResult, resetLink, badgeTopChoice, config, event } =
    useMemoizedContentGetter(content, [
      'titleResult',
      'eyebrowResult',
      'gridResult',
      'resetLink',
      'badgeTopChoice',
      'config',
      'event',
    ])

  const formResults = _get(config, `jsonContent.formResults["${formHomeowner}"]`, null)

  useEffect(() => {
    let matchResult = null
    if (formResults) {
      let i = 0
      while (matchResult === null && i < formResults.length) {
        const element = formResults[i]
        if (formAmount >= element.start && formAmount <= (element.end ?? formAmount))
          matchResult = element
        i++
      }
    }
    setTopChoice(matchResult?.order?.[0] ?? '')
    setOrderCards(matchResult?.order)
  }, [formResults, formAmount, formHomeowner])

  useEffect(() => {
    let copyGridItems = JSON.parse(JSON.stringify(gridResult?.gridItemsCollection?.items))
    if (orderCards) {
      copyGridItems = orderCards.map(
        (i) => copyGridItems[copyGridItems.map((e) => e?.fieldName).indexOf(i)]
      )
    }
    const listOrdered = []
    let matchChoice = null
    for (let i = 0; i < copyGridItems.length; i++) {
      const element = copyGridItems[i]
      if (element?.fieldName === topChoice) matchChoice = element
      else listOrdered.push(element)
    }
    if (topChoice && isMobile) {
      if (matchChoice) listOrdered.unshift(matchChoice)
      setGridItems(listOrdered)
    } else {
      if (matchChoice) listOrdered.splice(1, 0, matchChoice)
      setGridItems(listOrdered)
    }
  }, [topChoice, gridResult, isMobile, orderCards])

  const resetLinkText = _get(resetLink, 'textContent.json.content[0].content[0].value')

  const Card = ({ item, index, formAmount, formHomeowner }) => {
    const fields = item
    const eyebrowText = fields?.eyebrow
    const cardTitleText = _get(
      fields?.cardTitleText,
      'fields.textContent.json.content[0].content[0].value'
    )
    const cardLinkEvent = _get(fields?.event, 'fields.event')
    const cardLinkText = _get(fields?.cardLink, 'linkText.json.content[0].content[0].value')
    const cardLinkHref =
      _get(fields?.cardLink, 'linkHref') +
      `&${formAmountQueryParameter}=${formAmount}&is_home_owner=${formHomeowner}`
    return (
      <Box
        key={`item-${index}`}
        className={
          'flex flex-col gap-4 border min-w-[320px] max-w-xs bg-white shadow-[0_0.5px_3px_0_#10182817] items-center relative px-6 py-8 rounded-md border-solid border-neutral-150 group-data-[variant="carousel"]:min-w-[280px] group-data-[variant="carousel"]:max-w-[280px] group-data-[variant="carousel"]:mt-10 group-data-[variant="carousel"]:mb-1 md:group-data-[variant="carousel"]:min-w-[380px]md:group-data-[variant="carousel"]:max-w-[380px] xl:min-w-[380px] xl:min-h-[380px]'
        }
        data-top-choice={fields?.fieldName === topChoice}
        data-testid="cards-list-item"
      >
        {fields?.fieldName === topChoice && (
          <AchieveBadge
            content={badgeTopChoice?.mediaContent}
            height={70}
            width={70}
            anchor={ANCHOR_POINTS.RIGHT_TOP}
            translateX={-70}
            translateY={0}
          />
        )}
        <Box className={'w-[70px] h-[60px]'}>
          <MediaImageStandard
            alt={eyebrowText ?? fields?.cardImage?.title}
            fetchPriority="high"
            preload={true}
            content={fields?.cardImage}
            layout="responsive"
          />
        </Box>
        {eyebrowText && (
          <Eyebrow
            className={'pb-0'}
            content={eyebrowText}
            variant="bodySm"
            fontWeight="bold"
            black
          />
        )}
        <Typography content={fields?.cardTitleText} variant="bodyLg" fontWeight="medium" />
        <Box className={'flex flex-col border border-neutral-75 p-5 rounded-xl border-solid'}>
          {fields?.iconCardItemCollection?.items?.map((icon, iconIdx) => {
            return (
              <Box className={'flex flex-row'} key={`item-icon-${iconIdx}`}>
                <IconListItem
                  noList
                  key={`icon-list-item-${iconIdx}`}
                  cardTitleString={cardTitleText}
                  iconColor={icon.iconColor}
                  iconName={icon.iconName}
                  itemText={icon.description}
                  classNameIcon={''}
                  iconSize={20}
                  noMarginBottom={iconIdx + 1 === fields?.iconCardItem?.length}
                />
              </Box>
            )
          })}
        </Box>
        <LinkButton
          track={{
            ...{
              list_name: 'Homepage Skinny Form Result Buttons',
              click_id: cardLinkText,
              click_text: cardLinkText,
              click_position: index,
              form_ss_amount: formAmount,
              form_ss_homeowner: formHomeowner,
              product_card_title: eyebrowText,
              product_card_recomended: fields?.fieldName === topChoice,
              click_type: 'Button Click',
              nav_link_section: 'Recommended Cards',
              track_event: 'internal_campaign_click',
              event_action: 'button_click',
            },
            ...cardLinkEvent,
            sectionVariationObject,
          }}
          content={{
            linkHref: cardLinkHref,
            linkText: cardLinkText,
          }}
          typographicOptions={{ variant: 'bodyLg', fontWeight: 'bold' }}
          color="primary"
          variant="contained"
        />
      </Box>
    )
  }

  const itemsGrid = () => {
    return (
      <Box
        className={
          'flex flex-col gap-4 justify-center items-center mb-8 xl:[align-items:normal] xl:flex-row'
        }
      >
        {gridItems.map((item, i) => (
          <Card
            key={`item-${i}`}
            item={item}
            index={i}
            formAmount={formAmount}
            formHomeowner={formHomeowner}
          />
        ))}
      </Box>
    )
  }

  const itemGridCarousel = () => {
    return (
      <Box data-variant="carousel" className="group">
        <Carousel
          carouselItems={gridItems}
          itemComponent={Card}
          variant="auto"
          idPrefix="value-props-carousel"
          withIndicators
          className={'mb-6 p-0'}
          classNameItems={'pl-0!'}
          classNameContainer={'gap-4'}
          classNameIndicators={''}
          classNameActiveDot={''}
          dataItem={{
            formAmount,
            formHomeowner,
            topChoice: 'PL',
          }}
          disableArrows
        />
      </Box>
    )
  }

  return (
    <Box data-testid="skinny-form-section-recommended-cards">
      <TitleDivider
        className={'mb-0 xl:mb-12'}
        eyebrowClassName={'pb-0'}
        eyebrow={eyebrowResult}
        eyebrowBlack
        // subtitle={!isMobile && summaryResult}
        title={titleResult}
        isMobile={isMobile}
        data-testid="skinny-form-title"
        classNameTitleText="headingMd lg:displayLg font-medium!"
        classNameSubtitleText="bodyLg font-light!"
        subtitleTag="p"
      />
      {isMobile ? itemGridCarousel() : itemsGrid()}
      <TrackingWrapper
        track={{
          ...{
            list_name: 'Homepage Skinny Form Result',
            click_id: resetLinkText,
            click_text: resetLinkText,
            click_position: 4,
            form_ss_amount: formAmount,
            form_ss_homeowner: formHomeowner,
            click_type: 'Link Click',
            nav_link_section: 'Recommended Cards',
            event_action: 'link_click',
          },
          ...event,
          sectionVariationObject,
        }}
      >
        <Link
          className={'flex gap-1 items-center justify-center'}
          underline="hover"
          fontWeight="bold"
          variant="bodyLg"
          data-testid="mad-lib-results-reset"
          tabIndex={0}
          onClick={() => onReset()}
        >
          <ArrowLeft /> <span>{resetLinkText}</span>
        </Link>
      </TrackingWrapper>
    </Box>
  )
}

export { RecommendedCards }
export default RecommendedCards
